/**
 * Day Diary v2 mockup fixtures (task 237).
 *
 * Deterministic fixture data for the v2 surface — the progress batches and the
 * fallback-batch reclamation suggestions (one per variant). Consumed by
 * `useDayDiaryV2` in mockup mode so the page, the a11y gate, and the component
 * tests render without contacting the backend.
 *
 * Dates derive from FROZEN_NOW per the frontend fixture rule. Invented data
 * only — no real PII.
 */

import { FROZEN_NOW } from '@/lib/frozenTime';
import type { DiaryProjectBatchStatus, DiarySuggestion } from '@/types/dayDiaryV2';

const DAY = FROZEN_NOW.slice(0, 10);
const at = (hhmm: string) => `${DAY}T${hhmm}:00.000Z`;

export const DAY_DIARY_V2_BATCHES_IN_FLIGHT: readonly DiaryProjectBatchStatus[] = [
  {
    id: 'batch-meridian',
    projectId: 'prj-meridian-mna',
    projectName: 'M&A Diligence',
    clientName: 'Meridian Capital',
    status: 'consolidating',
    eventCount: 42,
  },
  {
    id: 'batch-northwind',
    projectId: 'prj-northwind-audit',
    projectName: 'Year-End Audit',
    clientName: 'Northwind Logistics',
    status: 'in_progress',
    eventCount: 18,
  },
  {
    id: 'batch-fallback',
    projectId: null,
    projectName: null,
    clientName: null,
    status: 'pending',
    eventCount: 5,
  },
];

export const DAY_DIARY_V2_BATCHES_COMPLETE: readonly DiaryProjectBatchStatus[] =
  DAY_DIARY_V2_BATCHES_IN_FLIGHT.map((b) => ({ ...b, status: 'complete' }));

export const DAY_DIARY_V2_SUGGESTIONS: readonly DiarySuggestion[] = [
  {
    id: 'sugg-existing',
    eventId: 'evt-existing',
    variant: 'existing_activity',
    confidence: 0.86,
    captured: {
      occurredAt: at('10:15'),
      app: 'Microsoft Word',
      title: 'Meridian SPA — redline v4',
      url: null,
      host: null,
    },
    observed: 'A Word document titled “Meridian SPA — redline v4” with tracked changes visible.',
    observedText: ['Meridian SPA', 'redline v4', 'Section 7.2'],
    lowSignal: false,
    attribution: {
      activityName: 'SPA drafting',
      clientName: 'Meridian Capital',
      projectName: 'M&A Diligence',
      notBillable: false,
    },
    screenshotId: 'shot-existing',
  },
  {
    id: 'sugg-new',
    eventId: 'evt-new',
    variant: 'new_activity',
    confidence: 0.62,
    captured: {
      occurredAt: at('11:40'),
      app: 'Chrome',
      title: 'Northwind — freight rate model',
      url: 'https://sheets.example.com/northwind-rates',
      host: 'sheets.example.com',
    },
    observed: 'A spreadsheet modelling freight rates with a Northwind tab open.',
    observedText: ['freight rate', 'Northwind', 'Q3 forecast'],
    lowSignal: false,
    attribution: {
      activityName: 'Freight rate modelling',
      clientName: 'Northwind Logistics',
      projectName: null,
      notBillable: false,
    },
    screenshotId: 'shot-new',
  },
  {
    id: 'sugg-personal',
    eventId: 'evt-personal',
    variant: 'personal',
    confidence: 0.91,
    captured: {
      occurredAt: at('12:30'),
      app: 'Chrome',
      title: 'Lunch menu — corner cafe',
      url: 'https://example-cafe.test/menu',
      host: 'example-cafe.test',
    },
    observed: 'A cafe menu page open in the browser.',
    observedText: ['lunch menu', 'cafe'],
    lowSignal: false,
    attribution: {
      activityName: 'Personal activity',
      clientName: 'Personal',
      projectName: 'Personal',
      notBillable: true,
    },
    screenshotId: 'shot-personal',
  },
  {
    id: 'sugg-unattributable',
    eventId: 'evt-unattributable',
    variant: 'unattributable',
    confidence: 0.34,
    captured: {
      occurredAt: at('14:05'),
      app: 'Finder',
      title: null,
      url: null,
      host: null,
    },
    observed: null,
    observedText: [],
    lowSignal: true,
    attribution: null,
    screenshotId: 'shot-unattributable',
  },
];
